/**
 * LA TABLE DE PROGRESSION.
 *
 * C'est le coeur du module, et c'est volontairement une table: la difficulte
 * doit se lire d'un coup d'oeil, pas se deduire de vingt `if (niveau > 3)`
 * eparpilles dans les generateurs. On lit ici, ligne par ligne, ce qu'un enfant
 * rencontre en 1re annee et ce qu'il rencontre en 6e.
 *
 * Deux garanties tiennent a cette table:
 *   - un enfant de 1re annee ne croise JAMAIS une fraction;
 *   - un enfant de 6e ne se fait JAMAIS demander 2 + 3.
 *
 * Le `rang` (0, 1 ou 2) ordonne les activites A L'INTERIEUR d'un niveau. Avec
 * la taille des nombres (0 a 9), il donne le poids d'une question:
 *
 *     poids = niveau * 100 + rang * 10 + taille        (au plus niveau*100 + 29)
 *
 * donc TOUTE question d'un niveau pese moins que TOUTE question du niveau
 * suivant. C'est cette propriete que verrouille tests/unit/ecole.test.ts.
 */
import type { Activite } from './atelier.ts'
import type { Niveau, Sujet } from './types.ts'
import {
  addition,
  aireCarre,
  aireDisque,
  aireRectangle,
  aireTriangle,
  angleManquant,
  circonference,
  compterCotes,
  conversion,
  decimalMultiplication,
  decimalSomme,
  division,
  fractionDe,
  fractionSimplifier,
  fractionSomme,
  multiplication,
  nommerForme,
  perimetreCarre,
  perimetreRectangle,
  pourcentage,
  problemeArgent,
  problemeLongueur,
  problemePaquets,
  problemePartage,
  problemeRabais,
  problemeReste,
  problemeTotal,
  problemeVitesse,
  soustraction,
  typeAngle,
  volumePave,
} from './maths.ts'
import {
  accordAdjectif,
  conjugaison,
  contraire,
  homophones,
  orthographe,
  participeAvoir,
  participeEtre,
  pluriel,
  synonyme,
} from './francais.ts'

export const PROGRESSION: Record<Sujet, Record<Niveau, readonly Activite[]>> = {
  // --------------------------------------------------------- arithmetique
  arithmetique: {
    1: [
      { id: 'addition-20', rang: 0, faire: addition(2, 9, 20) },
      { id: 'soustraction-20', rang: 1, faire: soustraction(8, 20, 2) },
    ],
    2: [
      { id: 'addition-100', rang: 0, faire: addition(11, 60, 100) },
      { id: 'soustraction-100', rang: 1, faire: soustraction(40, 99, 11) },
    ],
    3: [
      { id: 'tables', rang: 0, faire: multiplication(2, 10, 2, 10) },
      { id: 'division-simple', rang: 1, faire: division(2, 10, 2, 10) },
    ],
    4: [
      { id: 'tables-12', rang: 0, faire: multiplication(6, 12, 6, 12) },
      { id: 'multiplication-2-chiffres', rang: 1, faire: multiplication(12, 99, 3, 9) },
      { id: 'division-exacte', rang: 2, faire: division(4, 12, 3, 12) },
    ],
    5: [
      { id: 'fraction-somme', rang: 0, faire: fractionSomme },
      { id: 'fraction-de', rang: 1, faire: fractionDe },
      { id: 'decimal-somme', rang: 2, faire: decimalSomme },
    ],
    6: [
      { id: 'pourcentage', rang: 0, faire: pourcentage },
      { id: 'decimal-multiplication', rang: 1, faire: decimalMultiplication },
      { id: 'fraction-simplifier', rang: 2, faire: fractionSimplifier },
    ],
  },

  // ------------------------------------------------------------ geometrie
  geometrie: {
    1: [
      { id: 'compter-cotes', rang: 0, faire: compterCotes },
      { id: 'nommer-forme', rang: 1, faire: nommerForme },
    ],
    2: [
      { id: 'perimetre-carre', rang: 0, faire: perimetreCarre },
      { id: 'conversion-m-cm', rang: 1, faire: conversion('mètres', 'centimètres', 'cm', 100) },
    ],
    3: [
      { id: 'perimetre-rectangle', rang: 0, faire: perimetreRectangle },
      { id: 'conversion-km-m', rang: 1, faire: conversion('kilomètres', 'mètres', 'm', 1000) },
    ],
    4: [
      { id: 'aire-carre', rang: 0, faire: aireCarre },
      { id: 'aire-rectangle', rang: 1, faire: aireRectangle },
      { id: 'conversion-kg-g', rang: 2, faire: conversion('kilogrammes', 'grammes', 'g', 1000) },
    ],
    5: [
      { id: 'angle-type', rang: 0, faire: typeAngle },
      { id: 'aire-triangle', rang: 1, faire: aireTriangle },
      { id: 'angle-manquant', rang: 2, faire: angleManquant },
    ],
    6: [
      { id: 'circonference', rang: 0, faire: circonference },
      { id: 'aire-disque', rang: 1, faire: aireDisque },
      { id: 'volume-pave', rang: 2, faire: volumePave },
    ],
  },

  // ------------------------------------------------------------ problemes
  problemes: {
    1: [
      { id: 'total', rang: 0, faire: problemeTotal(3, 9, 2, 9) },
      { id: 'reste', rang: 1, faire: problemeReste(8, 20, 2) },
    ],
    2: [
      { id: 'total', rang: 0, faire: problemeTotal(12, 50, 5, 40) },
      { id: 'reste', rang: 1, faire: problemeReste(30, 99, 11) },
    ],
    3: [
      { id: 'paquets', rang: 0, faire: problemePaquets(3, 9, 3, 9) },
      { id: 'partage', rang: 1, faire: problemePartage(2, 9, 2, 9) },
    ],
    4: [
      { id: 'paquets', rang: 0, faire: problemePaquets(11, 25, 3, 9) },
      { id: 'partage', rang: 1, faire: problemePartage(3, 12, 4, 12) },
    ],
    5: [
      { id: 'longueur', rang: 0, faire: problemeLongueur },
      { id: 'argent', rang: 1, faire: problemeArgent },
    ],
    6: [
      { id: 'vitesse', rang: 0, faire: problemeVitesse },
      { id: 'rabais', rang: 1, faire: problemeRabais },
    ],
  },

  // ---------------------------------------------------------- conjugaison
  conjugaison: {
    1: [{ id: 'present', rang: 0, faire: conjugaison(['present'], 1) }],
    2: [{ id: 'present', rang: 0, faire: conjugaison(['present'], 2) }],
    3: [{ id: 'present-futur', rang: 0, faire: conjugaison(['present', 'futur'], 3) }],
    4: [{ id: 'imparfait-futur', rang: 0, faire: conjugaison(['imparfait', 'futur'], 4) }],
    5: [{ id: 'imparfait-passe-compose', rang: 0, faire: conjugaison(['imparfait', 'passe-compose'], 5) }],
    6: [{ id: 'tous-les-temps', rang: 0, faire: conjugaison(['passe-compose', 'imparfait', 'futur'], 6) }],
  },

  // ----------------------------------------------------------- homophones
  homophones: {
    1: [{ id: 'a-et', rang: 0, faire: homophones(1) }],
    2: [{ id: 'on-ont', rang: 0, faire: homophones(2) }],
    3: [{ id: 'son-ou', rang: 0, faire: homophones(3) }],
    4: [{ id: 'ces-la', rang: 0, faire: homophones(4) }],
    5: [{ id: 'ce-peu-sa', rang: 0, faire: homophones(5) }],
    6: [{ id: 'mes-leur-quel', rang: 0, faire: homophones(6) }],
  },

  // -------------------------------------------------------------- accords
  accords: {
    1: [{ id: 'adjectif-nombre', rang: 0, faire: accordAdjectif(1, false) }],
    2: [{ id: 'adjectif-genre', rang: 0, faire: accordAdjectif(2, true) }],
    3: [{ id: 'adjectif-irregulier', rang: 0, faire: accordAdjectif(3, true) }],
    4: [
      { id: 'participe-avoir', rang: 0, faire: participeAvoir },
      { id: 'adjectif-irregulier', rang: 1, faire: accordAdjectif(3, true) },
    ],
    5: [
      { id: 'participe-etre', rang: 0, faire: participeEtre },
      { id: 'participe-avoir', rang: 1, faire: participeAvoir },
    ],
    6: [
      { id: 'participe-etre', rang: 0, faire: participeEtre },
      { id: 'participe-avoir', rang: 1, faire: participeAvoir },
      { id: 'adjectif-irregulier', rang: 2, faire: accordAdjectif(3, true) },
    ],
  },

  // ---------------------------------------------------------- vocabulaire
  vocabulaire: {
    1: [
      { id: 'contraire', rang: 0, faire: contraire(1) },
      { id: 'pluriel', rang: 1, faire: pluriel(1) },
    ],
    2: [
      { id: 'contraire', rang: 0, faire: contraire(2) },
      { id: 'pluriel', rang: 1, faire: pluriel(2) },
      { id: 'orthographe', rang: 2, faire: orthographe(2) },
    ],
    3: [
      { id: 'contraire', rang: 0, faire: contraire(3) },
      { id: 'pluriel', rang: 1, faire: pluriel(3) },
      { id: 'synonyme', rang: 2, faire: synonyme(3) },
    ],
    4: [
      { id: 'pluriel', rang: 0, faire: pluriel(4) },
      { id: 'synonyme', rang: 1, faire: synonyme(4) },
      { id: 'orthographe', rang: 2, faire: orthographe(4) },
    ],
    5: [
      { id: 'pluriel', rang: 0, faire: pluriel(5) },
      { id: 'synonyme', rang: 1, faire: synonyme(5) },
      { id: 'orthographe', rang: 2, faire: orthographe(5) },
    ],
    6: [
      { id: 'synonyme', rang: 0, faire: synonyme(6) },
      { id: 'orthographe', rang: 1, faire: orthographe(6) },
    ],
  },
}
